import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { InterceptToken, Logout } from '@core/services/core/auth/auth.actions';
import { AuthService } from '@core/services/core/auth/auth.service';
import { ContextService } from '@core/services/core/context.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { TecuidamosService } from '@core/services/core/tecuidamos.service';
import { Observable, throwError } from 'rxjs';
import { catchError, first, mergeMap, share, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TokenInterceptorService implements HttpInterceptor {

  private jwt = new JwtHelperService();
  private refreshToken$: Observable<any>;

  constructor(
    private auth: AuthService,
    private nav: NavigationHelper,
    private ctx: ContextService,
    private teCuidamos: TecuidamosService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isPrivateArea =
      req.url.indexOf('middlewarePrivateOIMServices') >= 0 ||
      req.url.indexOf('middlewarePrivateTeCuidamosServices') >= 0;
    if (isPrivateArea) {
      const { user } = this.auth.getState();
      const isTokenExpired = this.jwt.isTokenExpired(user?.token);
      const isRefreshTokenExpired = this.jwt.isTokenExpired(user?.refreshToken);

      if (isTokenExpired && !isRefreshTokenExpired) {
        return this.refreshTokensAndCallRequest(req, next, isPrivateArea, user?.refreshToken);
      } else {
        req = this.addTokenHeader(req, user && user.token);
      }
    }
    return this.callRequest(req, next, isPrivateArea);
  }

  private addTokenHeader(req: HttpRequest<any>, token: string) {
    let headers = req.headers || new HttpHeaders();
    headers = headers.set('X-Authorization', `Bearer ${token}`);
    return req.clone({ headers });
  }

  private refreshTokensAndCallRequest<T>(request: HttpRequest<T>, next: HttpHandler, isPrivateArea: boolean, refreshToken: string) {
    return this.refreshToken(refreshToken).pipe(
      switchMap(() => {
        const { user } = this.auth.getState();
        request = this.addTokenHeader(request, user && user.token);
        return this.callRequest(request, next, isPrivateArea);
      }),
      catchError(refreshError => throwError(refreshError))
    );
  }

  private refreshToken(refreshToken: string) {
    if (!this.refreshToken$) {
      this.refreshToken$ = this.auth.refreshAccessToken(refreshToken).pipe(
        first(),
        mergeMap(data => {
          if (data?.token) {
            return this.auth.dispatch$(new InterceptToken({ token: data.token })).pipe(
              switchMap(() => {
                return this.teCuidamos.loginTeCuidamos();
              })
            );
          }
          return throwError(`Couldn't obtain new token`);
        }),
        share()
      );
    }
    return this.refreshToken$;
  }

  private callRequest<T>(req: HttpRequest<T>, next: HttpHandler, isPrivateArea: boolean) {
    return next.handle(req).pipe(
      tap((res: any) => {
        const token =
          res && res.headers && res.headers.get('MMOBILE_JWT_ACCESS_TOKEN');
        if (!token || !this.auth.getState().user) { return; }
        // Updates token with new claims I.e: TeCuidamos payload
        if (this.auth.getState().user.token !== token) {
          this.auth.dispatch(new InterceptToken({ token }));
        }
      }),
      catchError(error => {
        return this.onError(isPrivateArea, error);
      })
    );
  }

  private onError(isPrivateArea: boolean, error: any) {
    if (isPrivateArea && [401, 403].includes(error?.status)) {
      const { user } = this.auth.getState();
      const { token } = user || ({} as any);
      if (token && this.jwt.isTokenExpired(token)) {
        const isParticular = this.ctx.isAccessTypeParticular;
        this.auth.dispatch$(new Logout({ softLogout: true })).subscribe(
          () => {
            this.nav.goLoginPage(isParticular, { queryParams: { manual: true } });
          }
        );
      }
    }
    return throwError(error);
  }
}
